import { useState, useCallback, useMemo } from "react";

interface UseAssetSelectionReturn {
  selectedAssetIds: string[];
  toggleAsset: (assetId: string) => void;
  selectAll: () => void;
  deselectAll: () => void;
  isSelected: (assetId: string) => boolean;
  selectedCount: number;
}

export const useAssetSelection = (
  availableAssetIds: string[],
): UseAssetSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleAsset = useCallback((assetId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else {
        next.add(assetId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(availableAssetIds));
  }, [availableAssetIds]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (assetId: string) => selectedIds.has(assetId),
    [selectedIds],
  );

  const selectedAssetIds = useMemo(
    () => Array.from(selectedIds),
    [selectedIds],
  );

  const selectedCount = selectedIds.size;

  return {
    selectedAssetIds,
    toggleAsset,
    selectAll,
    deselectAll,
    isSelected,
    selectedCount,
  };
};
